// ============================================================
// TMR Portal Admin — editar um modelo de contrato (rota /modelos-contrato/:templateId)
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Notice } from '../../components/Notice';
import { PageHeader } from '../../components/ui';
import { ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { useDebounced } from '../../useDebounced';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  useArchiveTemplate,
  useContractTemplate,
  useContractTemplates,
  useOpenDraft,
  usePreviewTemplate,
  usePreviewTemplatePdf,
  usePublishVersion,
  useSaveDraft,
} from '../../data/contractTemplates/queries';
import {
  globalChangedAfterOverride,
  isWriteConflict,
  templateScopeLabel,
} from '../../data/contractTemplates/status';
import { PublishVersionModal } from './PublishVersionModal';
import { TemplateHistory } from './TemplateHistory';
import { VariablePalette } from './VariablePalette';

/** How long the editor waits for quiet before asking for a preview (`24` §9). */
const PREVIEW_DELAY_MS = 600;

/**
 * The route's element: one editor per template.
 *
 * **Keyed by the template, on purpose.** The router keeps the same element when only the id in the URL
 * changes — which is what "Ver o global" does from an override — and the editor holds the text being
 * edited and the write version it is entitled to overwrite. Carried across, the global's editor would
 * open on the override's text and save it against the override's version. A fresh editor per template
 * makes that impossible rather than merely unlikely.
 */
export function ContractTemplateEditorPage() {
  const { templateId } = useParams<{ templateId: string }>();

  return <TemplateEditor key={templateId} templateId={templateId} />;
}

/**
 * Writing the text a patient signs.
 *
 * Three behaviours carried over from the v1.1's screen, and each one prevents a specific loss:
 *
 * - **The write version travels both ways.** Every save sends back the version the screen read, and a
 *   `409` writes nothing. Two people editing the same contract text all afternoon is the normal way this
 *   screen is used — without it, the second save silently discards the first (`24` §9).
 * - **Leaving with unsaved changes asks first**, and `Ctrl+S` saves. The text in this box is often the
 *   only copy of a paragraph somebody just wrote. Every way out this screen offers asks; closing or
 *   reloading the tab gets the browser's own prompt.
 * - **The variables are inserted, not typed.** A marker spelt by hand is refused by nothing the operator
 *   can see while writing.
 *
 * The preview is rendered from what is in the box rather than from what is stored — the operator is
 * looking at unsaved changes, and a preview of the saved version would show them something they are not
 * editing.
 */
function TemplateEditor({ templateId }: { templateId: string | undefined }) {
  const navigate = useNavigate();

  const canManage = usePermission(PERMISSIONS.contractTemplatesManage);

  const query = useContractTemplate(templateId);
  // The list, for the global template an override departs from. Already cached when the operator came from
  // the list, which is the usual way here.
  const list = useContractTemplates();
  const openDraft = useOpenDraft();
  const save = useSaveDraft();
  const publish = usePublishVersion();
  const archive = useArchiveTemplate();
  const preview = usePreviewTemplate();
  const pdf = usePreviewTemplatePdf();

  // The text being edited, and the write version this screen is entitled to overwrite. Both live here
  // rather than in the query cache: the cache holds what the server last said, and these hold what the
  // operator has in front of them.
  const [draftText, setDraftText] = useState<string | null>(null);
  const [writeVersion, setWriteVersion] = useState<number | null>(null);
  const [savedText, setSavedText] = useState('');
  const [conflict, setConflict] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  // Where the operator asked to go while holding unsaved text, held until they confirm.
  const [leavingTo, setLeavingTo] = useState<string | null>(null);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const template = query.data;
  const draft = template?.versions.find((version) => version.id === template.draftVersionId) ?? null;
  const current = template?.versions.find((version) => version.id === template.currentVersionId) ?? null;

  // Adopt the server's copy once, and again whenever the template itself changes — but never over text
  // the operator has already started editing. With no draft open, the box shows the version in force,
  // read-only: it is the text patients are signing, and an empty box labelled with its number would say
  // the opposite.
  const shownText = draft?.content ?? current?.content ?? '';
  useEffect(() => {
    if (!template) return;
    setWriteVersion((held) => held ?? template.version);
    setDraftText((held) => held ?? shownText);
    setSavedText(shownText);
  }, [template, shownText]);

  const dirty = draftText !== null && draftText !== savedText;
  const debouncedText = useDebounced(draftText ?? '', PREVIEW_DELAY_MS);

  // Re-rendered after each pause in typing. Not a query: it is a POST that writes nothing, over text that
  // would be stale by the next keystroke.
  const renderPreview = preview.mutate;
  useEffect(() => {
    if (!template) return;
    renderPreview({ documentType: template.documentType, content: debouncedText });
  }, [template, debouncedText, renderPreview]);

  /** Saves the draft; resolves to whether it was saved, so publishing can stop when it was not. */
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!template || !draft || writeVersion === null || draftText === null) return false;

    try {
      const written = await save.mutateAsync({
        templateId: template.id,
        versionId: draft.id,
        content: draftText,
        expectedVersion: writeVersion,
      });

      setWriteVersion(written.version);
      setSavedText(draftText);
      setSaveNotice('Rascunho salvo.');
      setConflict(null);
      return true;
    } catch (error) {
      // A conflict is not an error to shrug at: nothing was saved, and the box holds the only copy of
      // what was written. The operator is told to copy it out before anything reloads.
      setConflict(
        isWriteConflict(error)
          ? (error as ApiError).detail
          : error instanceof ApiError
            ? error.detail
            : 'Não foi possível salvar o rascunho.',
      );
      return false;
    }
  }, [template, draft, writeVersion, draftText, save]);

  /** Every in-screen way out goes through here, so none of them drops unsaved text without asking. */
  function leaveTo(path: string) {
    if (dirty) setLeavingTo(path);
    else navigate(path);
  }

  /**
   * Publishing starts by saving what is on screen, as the v1.1 did: what gets published is the stored
   * draft, and asking the operator to press a second button first only adds a way to publish the wrong text.
   */
  async function startPublishing() {
    if (dirty && !(await handleSave())) return;
    setPublishing(true);
  }

  // Ctrl+S, because this is a text editor and people save text editors that way.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (dirty && canManage) void handleSave();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dirty, canManage, handleSave]);

  // Closing the tab with unsaved text. The browser's own dialog is the only one that can stop it.
  useEffect(() => {
    if (!dirty) return;

    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      // Required by Chrome for the dialog to appear; the wording is the browser's, not ours.
      event.returnValue = '';
    }

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  function insertMarker(marker: string) {
    const box = textAreaRef.current;
    const text = draftText ?? '';
    const token = `{{${marker}}}`;

    if (!box) {
      setDraftText(text + token);
      return;
    }

    const start = box.selectionStart;
    const end = box.selectionEnd;
    setDraftText(text.slice(0, start) + token + text.slice(end));

    // Put the caret after what was just inserted, on the next frame — React has not re-rendered yet.
    requestAnimationFrame(() => {
      box.focus();
      box.setSelectionRange(start + token.length, start + token.length);
    });
  }

  if (query.isPending) return <div className="pa-screen" style={{ padding: 24 }}><Skeleton rows={8} /></div>;

  if (query.isError) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }

  if (!template) return null;

  // An override whose global text moved on after it was published — the trap of an exception, which does
  // not follow what it departed from. The list says so on the row; here it says which version and when.
  const templates = list.data?.templates ?? [];
  const summary = templates.find((item) => item.id === template.id);
  const global = template.companyId === null
    ? null
    : templates.find(
        (item) => item.companyId === null && item.documentType === template.documentType && item.status === 'Active',
      ) ?? null;
  const globalMoved = Boolean(
    global && summary && template.status === 'Active' && globalChangedAfterOverride(summary, templates),
  );

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow={`${template.documentTypeName} · ${templateScopeLabel({
          ...template,
          currentVersionNumber: null,
          publishedAt: null,
          hasDraft: false,
          updatedAt: null,
        })}`}
        title={template.name}
        desc={
          current
            ? `Versão ${current.number} é a vigente — é dela que os contratos são emitidos agora.`
            : 'Este modelo ainda não tem versão publicada, então nenhum contrato é emitido a partir dele.'
        }
        actions={
          <>
            <Btn variant="secondary" onClick={() => leaveTo('/modelos-contrato')}>
              Voltar
            </Btn>
            {canManage && template.status === 'Active' && !draft && (
              <Btn
                variant="secondary"
                icon={Icons.plus}
                disabled={openDraft.isPending || !current}
                title={current ? undefined : 'Publique o primeiro rascunho antes de abrir outro.'}
                onClick={() =>
                  void openDraft
                    .mutateAsync({ templateId: template.id, expectedVersion: writeVersion ?? template.version })
                    .then((written) => {
                      setWriteVersion(written.version);
                      setDraftText(current?.content ?? '');
                      setSavedText(current?.content ?? '');
                    })
                    .catch(() => setConflict('Não foi possível abrir um rascunho. Recarregue a tela.'))
                }
              >
                {openDraft.isPending ? 'Abrindo…' : 'Abrir rascunho'}
              </Btn>
            )}
            {canManage && draft && (
              <>
                <Btn variant="secondary" disabled={!dirty || save.isPending} onClick={() => void handleSave()}>
                  {save.isPending ? 'Salvando…' : 'Salvar'}
                </Btn>
                <Btn variant="primary" disabled={save.isPending} onClick={() => void startPublishing()}>
                  Publicar
                </Btn>
              </>
            )}
          </>
        }
      />

      {conflict && (
        <Notice tone="danger">
          <strong>Nada foi salvo.</strong> {conflict}{' '}
          <button
            type="button"
            className="pa-link-btn"
            style={{ font: 'inherit' }}
            onClick={() => void navigator.clipboard?.writeText(draftText ?? '')}
          >
            Copiar meu texto
          </button>{' '}
          antes de recarregar a tela.
        </Notice>
      )}

      {saveNotice && !dirty && !conflict && <Notice tone="success">{saveNotice}</Notice>}

      {template.status === 'Archived' && (
        <Notice tone="warning">
          Este modelo está arquivado. A clínica voltou a assinar pelo texto global — os contratos já
          emitidos mantêm a versão que usaram.
        </Notice>
      )}

      {globalMoved && global && (
        <Notice tone="warning">
          <strong>O modelo global mudou depois desta exceção.</strong> A versão {global.currentVersionNumber} do
          global foi publicada em {global.publishedAt ? new Date(global.publishedAt).toLocaleDateString('pt-BR') : '—'}.
          A exceção não acompanha o global: confira se a mudança vale também
          para {template.companyName ?? 'esta clínica'}.{' '}
          <button
            type="button"
            className="pa-link-btn"
            style={{ font: 'inherit' }}
            onClick={() => leaveTo(`/modelos-contrato/${global.id}`)}
          >
            Ver o global
          </button>
        </Notice>
      )}

      {!draft && template.status === 'Active' && (
        <Notice tone="neutral">
          Não há rascunho em edição. Abra um a partir da versão vigente para alterar o texto.
        </Notice>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(240px, 1fr)', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pa-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {draft ? `Rascunho (versão ${draft.number})` : `Versão ${current?.number ?? '—'} (somente leitura)`}
              </span>
              {dirty && <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>Alterações não salvas</span>}
            </div>

            <textarea
              ref={textAreaRef}
              value={draftText ?? ''}
              readOnly={!draft || !canManage}
              onChange={(event) => setDraftText(event.target.value)}
              spellCheck={false}
              style={{
                width: '100%',
                minHeight: 420,
                resize: 'vertical',
                padding: 12,
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                lineHeight: 1.65,
                background: draft && canManage ? 'var(--surface-default, #fff)' : 'var(--cream-100)',
              }}
            />

            {draft && canManage && (
              <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                Markdown: <code># título</code>, <code>**negrito**</code>, listas com <code>-</code> e tabelas
                com <code>|</code>. HTML não entra no contrato. <kbd>Ctrl</kbd>+<kbd>S</kbd> salva.
              </span>
            )}

            {preview.data && preview.data.problems.length > 0 && (
              <Notice tone="warning">
                <strong>O que impede publicar:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {preview.data.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </Notice>
            )}
          </div>

          <div className="pa-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Prévia</span>
              <Btn
                variant="secondary"
                size="sm"
                disabled={pdf.isPending || (preview.data?.problems.length ?? 1) > 0}
                title={
                  (preview.data?.problems.length ?? 0) > 0
                    ? 'A prévia em PDF só sai de um texto que poderia ser publicado.'
                    : undefined
                }
                onClick={() =>
                  void pdf
                    .mutateAsync({
                      documentType: template.documentType,
                      name: template.name,
                      content: draftText ?? '',
                    })
                    .catch(() => undefined)
                }
              >
                {pdf.isPending ? 'Gerando…' : 'Ver em PDF'}
              </Btn>
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
              Com os valores de exemplo do tipo de documento — não com dados de um paciente real.
            </span>

            {pdf.error != null && (
              <Notice tone="warning">
                {pdf.error instanceof ApiError && pdf.error.status === 503
                  ? 'A prévia em PDF não está disponível neste ambiente. O resto do editor segue funcionando.'
                  : pdf.error instanceof ApiError
                    ? pdf.error.detail
                    : 'Não foi possível gerar a prévia em PDF.'}
              </Notice>
            )}
            {preview.isPending && !preview.data ? (
              <Skeleton rows={4} />
            ) : preview.data ? (
              // The HTML is sanitized by the backend, which is where the Markdown is parsed: rendering it
              // here means the operator sees exactly what the issuing engine produces.
              <div
                className="pa-prose"
                style={{ fontSize: 14, lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: preview.data.html }}
              />
            ) : (
              <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Nada para mostrar ainda.</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pa-card" style={{ padding: 16 }}>
            <VariablePalette
              variables={template.variables}
              used={preview.data?.variablesUsed ?? []}
              onInsert={draft && canManage ? insertMarker : undefined}
            />
          </div>

          {canManage && template.companyId !== null && template.status === 'Active' && (
            <Btn variant="secondary" onClick={() => setArchiving(true)}>
              Arquivar exceção
            </Btn>
          )}
        </div>
      </div>

      <TemplateHistory template={template} />

      {publishing && draft && (
        <PublishVersionModal
          template={template}
          draftVersionId={draft.id}
          problems={preview.data?.problems ?? []}
          unsaved={dirty}
          publishing={publish.isPending}
          error={publish.error}
          onClose={() => setPublishing(false)}
          onPublish={(note) =>
            void publish
              .mutateAsync({
                templateId: template.id,
                versionId: draft.id,
                note,
                expectedVersion: writeVersion ?? template.version,
              })
              .then((written) => {
                setWriteVersion(written.version);
                setPublishing(false);
                setSaveNotice(`Versão ${draft.number} publicada.`);
              })
              .catch(() => undefined)
          }
        />
      )}

      {leavingTo && (
        <ConfirmDialog
          title="Sair sem salvar?"
          message="O rascunho tem alterações que não foram salvas. Se sair agora, elas se perdem."
          confirmLabel="Sair sem salvar"
          danger
          icon={Icons.alert}
          onClose={() => setLeavingTo(null)}
          onConfirm={() => {
            const path = leavingTo;
            setLeavingTo(null);
            navigate(path);
          }}
        />
      )}

      {archiving && (
        <ConfirmDialog
          title="Arquivar esta exceção?"
          message={
            <>
              A partir do próximo contrato, esta clínica volta a assinar pelo <strong>texto global</strong>.
              Os contratos já emitidos mantêm a versão que usaram.
            </>
          }
          confirmLabel="Arquivar"
          icon={Icons.alert}
          onClose={() => setArchiving(false)}
          onConfirm={() => {
            setArchiving(false);
            void archive
              .mutateAsync({ templateId: template.id, expectedVersion: writeVersion ?? template.version })
              .then((written) => setWriteVersion(written.version))
              .catch(() => setConflict('Não foi possível arquivar o modelo. Recarregue a tela.'));
          }}
        />
      )}
    </div>
  );
}
